// src/components/pages/Lamination1/charts/ExtruderEventTable2.tsx
import React, { useMemo, useState } from "react";
import {
  classifyWithHysteresis,
  screwBand,
  type ScrewState,
  type ScrewThresholds,
} from "../../../../utils/screwSpeed";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Collapse,
  IconButton,
  ButtonBase,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type InOut = "in" | "out";


export type ExtruderStatusPoint = {
  timestamp: number; // ms (local)
  status: 0 | 1; // 0 => out, 1 => in
};

export type SpeedPoint = {
  timestamp: number; // ms (local)
  value: number; // Hz
};

// The bands live in utils/screwSpeed so the diagrams classify identically;
// re-exported because this module's consumers have always imported the type
// from here.
export type { ScrewThresholds };

type EventRow = {
  extruder: string;
  inOut: InOut;
  screw: ScrewState;
  startTime: string;
  endTime: string;
  durationSeconds: number;
};

type Props = {
  extruderId: string; // "1" | "2"
  runData: ExtruderStatusPoint[]; // in/out series
  speedData: SpeedPoint[]; // screw speed (Hz) series (already filtered to finite)
  thresholds: ScrewThresholds;
  title?: string;
  defaultExpanded?: boolean;
};

// FILTER WINDOW (seconds) for in/purging blips
const PURGE_MIN = 30;
const PURGE_MAX = 33;

// Expanded height: ~20 rows, then the body scrolls under a sticky header.
const VISIBLE_ROWS = 20;
const ROW_HEIGHT = 53; // default MUI TableRow height
const HEAD_HEIGHT = 57;
const TABLE_MAX_HEIGHT = HEAD_HEIGHT + VISIBLE_ROWS * ROW_HEIGHT;

const fmtDuration = (seconds: number): string => {
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)} hr`;
  if (seconds >= 60) return `${(seconds / 60).toFixed(1)} min`;
  return `${seconds} sec`;
};

const fmtScrew = (screw: string): string => {
  return screw === "idle" ? "purging" : screw;
};

/** Make change points for screw state from speed samples */
const compressScrewChanges = (
  speed: SpeedPoint[],
  t: ScrewThresholds
): { timestamp: number; screw: ScrewState }[] => {
  if (!speed.length) return [];
  const sorted = [...speed].sort((a, b) => a.timestamp - b.timestamp);

  let prev = screwBand(sorted[0].value, t);
  const out: { timestamp: number; screw: ScrewState }[] = [
    { timestamp: sorted[0].timestamp, screw: prev },
  ];

  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i];
    const next = classifyWithHysteresis(s.value, prev, t);
    if (next !== prev) {
      out.push({ timestamp: s.timestamp, screw: next });
      prev = next;
    }
  }
  return out;
};

/** Make change points for in/out status */
const compressInOutChanges = (
  run: ExtruderStatusPoint[]
): { timestamp: number; inOut: InOut }[] => {
  if (!run.length) return [];
  const sorted = [...run].sort((a, b) => a.timestamp - b.timestamp);
  let prev: InOut = sorted[0].status === 1 ? "in" : "out";
  const out: { timestamp: number; inOut: InOut }[] = [
    { timestamp: sorted[0].timestamp, inOut: prev },
  ];

  for (let i = 1; i < sorted.length; i++) {
    const p = sorted[i];
    const nxt: InOut = p.status === 1 ? "in" : "out";
    if (nxt !== prev) {
      out.push({ timestamp: p.timestamp, inOut: nxt });
      prev = nxt;
    }
  }
  return out;
};

const rowColor = (inOut: InOut, screw: ScrewState): string => {
  // Choose colors by COMBINATION
  if (inOut === "in" && screw === "running") return "#e8f5e9"; // green
  if (inOut === "in" && screw === "idle") return "#ffebee"; // amber-ish
  if (inOut === "in" && screw === "stopped") return "#ffebee"; // pale yellow
  if (inOut === "out" && screw === "running") return "#ffebee"; // alert red-tint
  if (inOut === "out" && screw === "idle") return "#ffebee"; // light red
  return "#ffebee"; // out + stopped
};

const ExtruderEventTable2: React.FC<Props> = ({
  extruderId,
  runData,
  speedData,
  thresholds,
  title,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const events = useMemo(() => {
    if (!runData.length || !speedData.length) return [] as EventRow[];

    const inOutChanges = compressInOutChanges(runData);
    const screwChanges = compressScrewChanges(speedData, thresholds);

    if (!inOutChanges.length || !screwChanges.length) return [] as EventRow[];

    // Build a merged, ordered list of all change points with latest states
    type Change = { t: number; inOut?: InOut; screw?: ScrewState };
    const merged: Change[] = [
      ...inOutChanges.map((c) => ({ t: c.timestamp, inOut: c.inOut })),
      ...screwChanges.map((c) => ({ t: c.timestamp, screw: c.screw })),
    ].sort((a, b) => a.t - b.t);

    // Walk, carrying the latest state for each dimension
    let curInOut: InOut = inOutChanges[0].inOut;
    let curScrew: ScrewState = screwChanges[0].screw;
    let startT = Math.min(
      merged[0].t,
      inOutChanges[0].timestamp,
      screwChanges[0].timestamp
    );

    const endT = Math.max(
      runData[runData.length - 1].timestamp,
      speedData[speedData.length - 1].timestamp
    );

    const rows: EventRow[] = [];

    for (let i = 0; i < merged.length; i++) {
      const ch = merged[i];
      const segEnd = ch.t;

      if (segEnd > startT) {
        rows.push({
          extruder: `Extruder ${extruderId}`,
          inOut: curInOut,
          screw: curScrew,
          startTime: new Date(startT).toLocaleString(),
          endTime: new Date(segEnd).toLocaleString(),
          durationSeconds: Math.round((segEnd - startT) / 1000),
        });
        startT = segEnd;
      }

      if (ch.inOut) curInOut = ch.inOut;
      if (ch.screw) curScrew = ch.screw;
    }

    // Final segment to endT
    if (endT > startT) {
      rows.push({
        extruder: `Extruder ${extruderId}`,
        inOut: curInOut,
        screw: curScrew,
        startTime: new Date(startT).toLocaleString(),
        endTime: new Date(endT).toLocaleString(),
        durationSeconds: Math.round((endT - startT) / 1000),
      });
    }

    return rows;
  }, [extruderId, runData, speedData, thresholds]);

  // 🔎 Filter out intermediary in/purging blips of ~30–33 seconds
  const filteredEvents = useMemo(() => {
    return events.filter((row) => {
      const isPurging = row.inOut === "in" && row.screw === "idle";
      const d = row.durationSeconds;
      const isBlip = isPurging && d >= PURGE_MIN && d <= PURGE_MAX;
      return !isBlip;
    });
  }, [events]);

  const heading = title ?? `Extruder ${extruderId} — Run Events`;
  const count = filteredEvents.length;

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <ButtonBase
        onClick={() => setExpanded((v) => !v)}
        disabled={!count}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1,
          px: 1.5,
          py: 1,
          textAlign: "left",
        }}
        aria-expanded={expanded}
      >
        <IconButton
          component="span"
          size="small"
          disableRipple
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2">{heading}</Typography>
        <Typography variant="caption" color="text.secondary">
          {count ? `${count} event${count === 1 ? "" : "s"}` : "No events"}
        </Typography>
      </ButtonBase>

      <Collapse in={expanded && count > 0} unmountOnExit>
        <Box display="flex" gap={2} px={2} pb={1} flexWrap="wrap">
          <Typography variant="subtitle2">Row colors:</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Legend swatch="#e8f5e9" label="in × running" />
            <Legend swatch="#ffebee" label="in × idle (purging)" />
            <Legend swatch="#ffebee" label="in × stopped" />
            <Legend swatch="#ffebee" label="out × running" />
            <Legend swatch="#ffebee" label="out × idle/stopped" />
          </Box>
        </Box>

        {/* ~20 rows visible; the rest scrolls under the sticky header */}
        <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Extruder</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Screw State</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>End Time</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Duration
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {[...filteredEvents].reverse().map((row, i) => (
                <TableRow
                  key={i}
                  sx={{ backgroundColor: rowColor(row.inOut, row.screw) }}
                >
                  <TableCell>{row.extruder}</TableCell>
                  <TableCell>{row.inOut}</TableCell>
                  <TableCell>{fmtScrew(row.screw)}</TableCell>
                  <TableCell>{row.startTime}</TableCell>
                  <TableCell>{row.endTime}</TableCell>
                  <TableCell align="right">
                    {fmtDuration(row.durationSeconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

// tiny legend chip
const Legend: React.FC<{ swatch: string; label: string }> = ({
  swatch,
  label,
}) => (
  <Box display="inline-flex" alignItems="center" gap={1}>
    <Box
      sx={{
        width: 14,
        height: 14,
        bgcolor: swatch,
        border: "1px solid #ddd",
        borderRadius: 0.5,
      }}
    />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

export default ExtruderEventTable2;
